import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { api } from "../lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.toLowerCase().trim() });
      setSent(true);
    } catch (err) {
      setError("Não foi possível processar o pedido. Tente novamente dentro de alguns instantes.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ronil-light px-6 py-12">
      {/* Decorative background gradient */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-32 w-96 h-96 rounded-full bg-ronil-navy/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-ronil-sand/10 blur-3xl" />
      </div>

      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-elevation p-8 sm:p-10"
        data-testid="forgot-form"
      >
        {/* Brand lockup */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-ronil-navy text-white font-display font-bold text-lg rounded-sm flex items-center justify-center">R</div>
          <div>
            <div className="font-heading font-black text-base tracking-[0.1em] text-ronil-navy">RONIL</div>
            <div className="text-[9px] text-slate-500 uppercase tracking-[0.3em]">Command Center</div>
          </div>
        </div>

        <div className="mb-7">
          <div className="text-[10px] uppercase tracking-[0.25em] text-ronil-sand font-heading font-bold mb-2 flex items-center gap-2">
            <Mail className="w-3 h-3" /> Recuperação de acesso
          </div>
          <h1 className="font-display text-3xl font-bold text-ronil-navy tracking-tight mb-2 leading-tight">Esqueceu-se da palavra-passe?</h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            Introduza o seu email Ronil. Se existir uma conta associada, enviaremos um link de redefinição válido por 30 minutos.
          </p>
        </div>

        {sent ? (
          <div data-testid="forgot-success" className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-md">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <div className="font-heading font-semibold mb-1">Pedido registado</div>
              <div className="text-emerald-800/90 leading-relaxed">
                Se o email <b>{email}</b> estiver associado a uma conta, receberá dentro de instantes
                uma mensagem com o link para definir uma nova palavra-passe. Verifique também a caixa de
                correio indesejado.
              </div>
            </div>
          </div>
        ) : (
          <>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold mb-2">
              Endereço de email
            </label>
            <input
              data-testid="forgot-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="utilizador@ronil-auto.co.mz"
              className="w-full mb-4 px-3.5 py-2.5 border border-slate-300 rounded-md bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-ronil-navy/30 focus:border-ronil-navy transition"
            />
            {error && (
              <div data-testid="forgot-error" className="mb-4 px-4 py-2.5 bg-red-50 text-red-800 border border-red-200 rounded-md text-sm">
                {error}
              </div>
            )}
            <button
              data-testid="forgot-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-ronil-navy hover:bg-ronil-navy-hover text-white rounded-md py-2.5 text-sm font-heading font-semibold tracking-wide transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm hover:shadow-elevation"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Enviar link de redefinição
            </button>
          </>
        )}

        <Link
          to="/login"
          data-testid="forgot-back-link"
          className="mt-7 inline-flex items-center gap-1.5 text-xs text-ronil-navy hover:text-ronil-navy-hover hover:underline font-medium"
        >
          <ArrowLeft className="w-3 h-3" />
          Voltar ao login
        </Link>
      </form>
    </div>
  );
}
